import React, {useContext, useState, useEffect} from 'react';
import ReactDOM from 'react-dom';
import {Trans, useTranslation} from "react-i18next";
import PlanName from "./PlanName.jsx";
import {DateTimeFormatter, LocalDateTime} from "@js-joda/core";
import {SubscriptionApi} from "../../../api/authority/generated/index.ts";
import {GlobalContext} from "../../../utils/context/GlobalContextProvider.jsx";
import {useRenderNumber} from "../../../api/utilities/useValueRenderers.js";

const ConfirmationModal = ({ isOpen, onConfirm, onClose, project, packageInfo, isAnnual }) => {
    const { getAuthorityConfiguration } = useContext(GlobalContext);
    const { t } = useTranslation();
    const [prorationAmount, setProrationAmount] = useState(0);

    let renderNumber = useRenderNumber();

    let type = 'new';
    let expiresString = null;
    if (packageInfo && project && project.subscription) {
        const subscription = project.subscription;
        const expires = LocalDateTime.parse(subscription.endDate);
        expiresString = expires.format(DateTimeFormatter.ofPattern('dd.MM.yyyy'));

        if (subscription.isFreeTrial) {
            type = 'free';
        } else {
            type = subscription.currentPackage.monthlyCzk > packageInfo.monthlyCzk ? 'downgrade' : 'upgrade';
        }
    }

    useEffect(() => {
        if (isOpen && type === 'upgrade') {
            const api = new SubscriptionApi(getAuthorityConfiguration());
            api.getSubscriptionChangePrice({
                projectId: project.id,
                packageId: packageInfo.id,
            })
                .then(response => {
                    if (response.amount > 0) {
                        setProrationAmount(response.amount / 100);
                    }
                })
                .catch(error => console.error("Failed to fetch proration amount:", error));
        }
    }, [isOpen, type, getAuthorityConfiguration, project, packageInfo]);

    if (!isOpen) return null;

    return ReactDOM.createPortal(
        <div className="modal__billing fixed inset-0 z-50 overflow-auto bg-black bg-opacity-50 flex">
            <div
                className="relative bg-white dark:bg-gray-800 w-full max-w-lg m-auto flex-col flex rounded-lg shadow-lg">
                <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700/60">
                    <div className="flex justify-between items-center">
                        <div className="font-semibold text-gray-800 dark:text-gray-100">
                            {t('subscription.package-change-title')}
                        </div>
                        <button
                            className="text-gray-400 dark:text-gray-500 hover:text-gray-500 dark:hover:text-gray-400"
                            onClick={onClose}
                        >
                            <div className="sr-only">Close</div>
                            <svg className="fill-current" width="16" height="16" viewBox="0 0 16 16">
                                <path
                                    d="M7.95 6.536l4.242-4.243a1 1 0 111.415 1.414L9.364 7.95l4.243 4.242a1 1 0 11-1.415 1.415L7.95 9.364l-4.243 4.243a1 1 0 01-1.414-1.415L6.536 7.95 2.293 3.707a1 1 0 011.414-1.414L7.95 6.536z"></path>
                            </svg>
                        </button>
                    </div>
                </div>
                <div className="px-5 py-4">
                    <div className={'text-sm text-gray-900 mb-2'}>
                        {t('subscription.confirm-message')}
                    </div>

                    <div
                        className="mb-3 w-full h-full text-left py-3 px-4 rounded-lg bg-white dark:bg-gray-800 border border-violet-400 dark:border-violet-500 shadow-sm"
                    >
                        <div className="flex items-center">
                            <div className="grow">
                                <div className="flex flex-wrap items-center justify-between mb-0.5">
                                    <span className="font-medium text-gray-800 dark:text-gray-100">
                                        <PlanName planName={packageInfo.name}/>
                                    </span>
                                    <span className="font-medium text-green-600">
                                        {renderNumber(isAnnual ? (packageInfo.yearlyCzk * 12) : packageInfo.monthlyCzk)} Kč
                                    </span>
                                </div>
                                <div className="text-xs text-right">
                                    <span>{t('subscription.modal-billing-text')} </span>
                                    <strong>
                                        {isAnnual ? t('payment.annually').toLowerCase() : t('payment.monthly').toLowerCase()}
                                    </strong>
                                </div>
                            </div>
                        </div>
                    </div>
                    {expiresString && (
                        <div className={'text-xs text-gray-600'}>
                            {type === 'free' && (
                                <span>
                                    <Trans
                                        i18nKey="subscription.free-active"
                                        values={{expires: expiresString}}
                                        components={{strong: <strong/>}}
                                    />
                                </span>
                            )}
                            {type === 'upgrade' && (
                                <span>
                                    <Trans
                                        i18nKey="subscription.upgrade-active"
                                        values={{expires: expiresString}}
                                        components={{strong: <strong/>}}
                                    />
                                    {prorationAmount > 0 && (
                                        <Trans
                                            i18nKey="subscription.proration-amount"
                                            values={{amount: renderNumber(prorationAmount)}}
                                            components={{strong: <strong/>}}
                                        />
                                    )}
                                </span>
                            )}
                            {type === 'downgrade' && (
                                <span>
                                    <Trans
                                        i18nKey="subscription.downgrade-active"
                                        values={{expires: expiresString}}
                                        components={{strong: <strong/>}}
                                    />
                                </span>
                            )}
                        </div>
                    )}
                </div>

                <div className="px-5 py-2 flex justify-between gap-1 border-t border-gray-200 dark:border-gray-700/60">
                    <button
                        className="btn bg-gray-500 text-white"
                        onClick={onClose}
                    >
                        {t('subscription.close')}
                    </button>
                    <button
                        className="btn bg-indigo-500 text-white"
                        onClick={onConfirm}
                    >
                        {project.subscription ? t('subscription.confirm') : t('subscription.try-14-days-free')}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default ConfirmationModal;